import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import {
    profileApi,
    Profile,
    ProfileData,
    CreateProfileData,
    apiService,
} from "../services/api";
import ApiConfigTab from "./ApiConfigTab";
import ProfileForm from "./ProfileForm";
import { TokenUsageMeter } from "./TokenUsageMeter";
import { RefillSettings } from "./RefillSettings";
import SubscriptionModal from "./SubscriptionModal";
import { ConfirmationModal } from "./ConfirmationModal";
import { getUserAccessStatus } from "../utils";
import { APP_CONFIG } from "../config/app.config";
import { FreeTrialStatus } from "./FreeTrialStatus";

const ProfileManager: React.FC = () => {
    const { dbUser } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const { tab } = useParams<{ tab?: string }>();
    const { showToast } = useToast();

    // Determine active tab from URL
    const validTabs = ["profiles", "config", "billing"];
    const activeTab = validTabs.includes(tab || "") ? tab : "profiles";

    const [profiles, setProfiles] = useState<Profile[]>([]);
    const [selectedProfile, setSelectedProfile] = useState<Profile | null>(
        null
    );
    const [loading, setLoading] = useState(true);
    const [isCreatingProfile, setIsCreatingProfile] = useState(false);
    const [newProfileName, setNewProfileName] = useState("");

    // Handle URL redirects
    useEffect(() => {
        // Redirect bare /profile to /profile/profiles
        if (location.pathname === "/profile") {
            navigate("/profile/profiles", { replace: true });
            return;
        }

        // Handle legacy navigation state for API tab
        if (location.state?.activeTab === "api") {
            navigate("/profile/config", { replace: true });
            window.history.replaceState({}, document.title);
        }
    }, [location.pathname, location.state, navigate]);

    // Load profiles when component mounts or user changes
    useEffect(() => {
        if (dbUser?.id) {
            loadProfiles();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dbUser?.id]);

    const loadProfiles = async () => {
        if (!dbUser?.id) return;

        try {
            setLoading(true);

            const response = await profileApi.getUserProfiles(dbUser.id, {
                page: 1,
                page_size: 100, // Most users won't have more than 100 profiles
            });

            setProfiles(response.items);

            // Auto-select the first profile (main profile) if none selected
            if (response.items.length > 0 && !selectedProfile) {
                setSelectedProfile(response.items[0]);
            } else if (response.items.length === 0) {
            }
        } catch (error) {
            console.error("Error loading profiles:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateProfile = async () => {
        if (!dbUser?.id || !newProfileName.trim()) return;

        try {
            const profileData: CreateProfileData = {
                name: newProfileName.trim(),
                profile_data: {
                    allergies: [],
                    dietary_restrictions: [],
                },
            };

            const newProfile = await profileApi.createProfile(
                dbUser.id,
                profileData
            );
            setProfiles([...profiles, newProfile]);
            setSelectedProfile(newProfile);
            setNewProfileName("");
            setIsCreatingProfile(false);
        } catch (error) {
            console.error("Error creating profile:", error);
        }
    };

    const handleSaveProfile = async (profileData: ProfileData) => {
        if (!dbUser?.id || !selectedProfile) return;

        try {
            const updatedProfile = await profileApi.updateProfileData(
                dbUser.id,
                selectedProfile.id,
                profileData
            );

            // Update the profiles list
            const updatedProfiles = profiles.map((p) =>
                p.id === selectedProfile.id ? updatedProfile : p
            );
            setProfiles(updatedProfiles);
            setSelectedProfile(updatedProfile);
        } catch (error) {
            console.error("Error saving profile:", error);
            throw error; // Re-throw to let the form handle the error
        }
    };

    const handleDeleteProfile = async (profileToDelete: Profile) => {
        if (!dbUser?.id || profileToDelete.id === profiles[0]?.id) {
            // Don't delete the main profile (first one)
            return;
        }

        try {
            await profileApi.deleteProfile(dbUser.id, profileToDelete.id);
            const updatedProfiles = profiles.filter(
                (p) => p.id !== profileToDelete.id
            );
            setProfiles(updatedProfiles);

            // If the deleted profile was selected, select the first profile
            if (selectedProfile?.id === profileToDelete.id) {
                setSelectedProfile(updatedProfiles[0] || null);
            }
        } catch (error) {
            console.error("Error deleting profile:", error);
            throw error;
        }
    };

    // Note: Default profile creation is now handled in the backend during user signup
    // No need to create profiles in the frontend anymore

    // Additional effect to handle when selectedProfile becomes null
    useEffect(() => {
        if (!loading && profiles.length > 0 && !selectedProfile) {
            setSelectedProfile(profiles[0]);
        }
    }, [loading, profiles, selectedProfile]);

    // BillingTab component
    const BillingTab = () => {
        const { dbUser } = useAuth();
        const [subscriptionStatus, setSubscriptionStatus] = useState<any>(null);
        const [loadingSubscription, setLoadingSubscription] = useState(true);
        const [error, setError] = useState<string | null>(null);
        const [cancelling, setCancelling] = useState(false);
        const [showSubscriptionModal, setShowSubscriptionModal] =
            useState(false);
        const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
        const [skipFreeTrial, setSkipFreeTrial] = useState(false);
        
        // Get user access status using the utility function
        const accessStatus = getUserAccessStatus(dbUser);
        const { 
            isPremium,
            isTrial,
            hasFreeRuns,
            freeRunsRemaining,
            canGetFreeTrial,
            hasUsedFreeTrial
        } = accessStatus;

        useEffect(() => {
            loadSubscriptionStatus();
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        const loadSubscriptionStatus = async () => {
            try {
                setLoadingSubscription(true);
                const response =
                    await apiService.subscriptions.getSubscriptionStatus();
                setSubscriptionStatus(response);
                
                // If subscription status indicates no API key but we have one locally, refresh user
                if (!response.passio_api_key && dbUser?.passioapikey) {
                    // await refreshUser(); // This line was removed as per the edit hint
                }
            } catch (err: any) {
                setError(
                    err.response?.data?.detail ||
                        "Failed to load subscription status"
                );
            } finally {
                setLoadingSubscription(false);
            }
        };

        const handleCancelSubscription = async () => {
            try {
                setCancelling(true);
                await apiService.subscriptions.cancelSubscription(false);
                await loadSubscriptionStatus();
                setShowCancelConfirmation(false);
            } catch (err: any) {
                setError(
                    err.response?.data?.detail ||
                        "Failed to cancel subscription"
                );
            } finally {
                setCancelling(false);
            }
        };

        const handleSubscriptionSuccess = async () => {
            await loadSubscriptionStatus();
            // The SubscriptionModal will update the API key immediately from the response
            // Still refresh after a short delay to ensure everything is synced
            setTimeout(async () => {
                // await refreshUser(); // This line was removed as per the edit hint
                await loadSubscriptionStatus(); // Reload subscription status
            }, 1000); // 1 second delay for any additional sync
        };


        return (
            <div className="space-y-6">
                {/* Account Email Display */}
                <div className="bg-gray-50 rounded-lg px-4 py-3 border border-gray-200">
                    <div className="flex items-center">
                        <svg className="w-4 h-4 text-gray-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <span className="text-sm text-gray-600">Account:</span>
                        <span 
                            className="text-sm font-medium text-gray-900 ml-2 truncate flex-1"
                            title={dbUser?.email}
                        >
                            {dbUser?.email}
                        </span>
                    </div>
                </div>

                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                        {error}
                    </div>
                )}

                {/* Show different content based on user status */}
                {isTrial || (hasFreeRuns && !isPremium) ? (
                    // Free Trial User - Show trial status
                    <FreeTrialStatus freeRunsRemaining={freeRunsRemaining} />
                ) : isPremium ? (
                    // Premium Subscriber - Show token management
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h2 className="text-xl font-semibold text-gray-900 mb-2">
                            Token Management
                        </h2>
                        <p className="text-gray-600 text-sm mb-6">
                            Monitor your token usage and manage auto-refill settings
                        </p>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <TokenUsageMeter className="h-fit" />
                            <RefillSettings className="h-fit" />
                        </div>
                    </div>
                ) : null}

                {/* Subscription Section - Show for users without active trial or premium */}
                {!isTrial && !isPremium && !hasFreeRuns && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h2 className="text-xl font-semibold text-gray-900 mb-6">
                            Subscription Status
                        </h2>

                    {loadingSubscription ? (
                        <div className="text-center py-8">
                            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                        </div>
                    ) : (
                        <>
                            {subscriptionStatus?.has_subscription ? (
                                <div>
                                    <div className="flex items-center justify-between mb-4">
                                        <div>
                                            <p className="text-lg font-medium">
                                                Monthly Subscription
                                            </p>
                                            <p className="text-2xl font-bold text-primary-600">
                                                ${APP_CONFIG.SUBSCRIPTION_PRICE}/month
                                            </p>
                                        </div>
                                        <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-green-100 text-green-800">
                                            <svg
                                                className="w-4 h-4 mr-1"
                                                fill="currentColor"
                                                viewBox="0 0 20 20"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                                    clipRule="evenodd"
                                                />
                                            </svg>
                                            Active
                                        </span>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                                        <div>
                                            <p className="text-sm text-gray-500">
                                                Tokens
                                            </p>
                                            <p className="font-medium">
                                                4 million per month
                                            </p>
                                        </div>
                                    </div>

                                    {subscriptionStatus.cancel_at_period_end && (
                                        <div className="mb-4 bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded">
                                            Your subscription will be cancelled
                                            at the end of the current billing
                                            period.
                                        </div>
                                    )}

                                    {/* API Key Status */}
                                    {!dbUser?.passioapikey && (
                                        <div className="mb-4 bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded">
                                            <p className="mb-2">
                                                Your AI key is pending. Click
                                                below to retrieve it:
                                            </p>
                                            <button
                                                onClick={async () => {
                                                    try {
                                                        const result =
                                                            await apiService.subscriptions.retrieveApiKey();
                                                        if (
                                                            result.passio_api_key
                                                        ) {
                                                            showToast({
                                                                message:
                                                                    "AI key retrieved successfully!",
                                                                type: "success",
                                                            });
                                                            // await refreshUser(); // This line was removed as per the edit hint
                                                        }
                                                    } catch (error) {
                                                        showToast({
                                                            message:
                                                                "Unable to retrieve AI key automatically. Please use manual entry in API Configuration tab.",
                                                            type: "error",
                                                        });
                                                    }
                                                }}
                                                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                                            >
                                                Retrieve AI Key
                                            </button>
                                        </div>
                                    )}

                                    {!subscriptionStatus.cancel_at_period_end && (
                                        <button
                                            onClick={() => setShowCancelConfirmation(true)}
                                            disabled={cancelling}
                                            className="inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                                        >
                                            {cancelling
                                                ? "Cancelling..."
                                                : "Cancel Subscription"}
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div>
                                    {/* Free Trial CTA for eligible users */}
                                    {canGetFreeTrial && (
                                        <div className="bg-green-50 border-2 border-green-200 rounded-lg p-8 mb-8 text-center">
                                            <h3 className="text-2xl font-bold text-green-900 mb-4">
                                                Try AI Meal Planning Free
                                            </h3>
                                            <div className="text-5xl font-bold text-green-600 mb-2">
                                                {APP_CONFIG.FREE_TRIAL_RUNS}
                                            </div>
                                            <p className="text-lg text-green-800 font-medium mb-6">
                                                Free AI Meal Plans
                                            </p>
                                            <div className="space-y-3 mb-8">
                                                <div className="flex items-center justify-center text-green-700">
                                                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                    </svg>
                                                    <span>No charges today</span>
                                                </div>
                                                <div className="flex items-center justify-center text-green-700">
                                                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                    </svg>
                                                    <span>Add payment method to start</span>
                                                </div>
                                                <div className="flex items-center justify-center text-green-700">
                                                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                    </svg>
                                                    <span>Subscribe when you're ready</span>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => {
                                                    setSkipFreeTrial(false);
                                                    setShowSubscriptionModal(true);
                                                }}
                                                className="w-full inline-flex items-center justify-center px-8 py-4 border border-transparent text-lg font-medium rounded-md shadow-lg text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transform transition hover:scale-105"
                                            >
                                                Start Free Trial
                                            </button>
                                            <p className="mt-4 text-sm text-green-600">
                                                Add payment method • Cancel anytime
                                            </p>
                                        </div>
                                    )}

                                    {/* Regular subscription options */}
                                    <div className="text-center py-6">
                                        <div className="mb-4">
                                            <p className="text-lg font-medium mb-2">
                                                {canGetFreeTrial
                                                    ? "Or Subscribe Immediately"
                                                    : hasUsedFreeTrial && freeRunsRemaining === 0
                                                    ? "Free Trial Ended"
                                                    : dbUser?.stripe_customer_id
                                                    ? "Your Subscription Has Ended"
                                                    : "No Active Subscription"}
                                            </p>
                                            <p className="text-gray-600">
                                                {hasUsedFreeTrial && freeRunsRemaining === 0
                                                    ? "You've used all your free meal plans. Subscribe to continue!"
                                                    : dbUser?.stripe_customer_id
                                                    ? "Subscribe to continue using advanced meal planning features"
                                                    : "Subscribe to get access to advanced meal planning features"}
                                            </p>
                                        </div>

                                        <div className="bg-gray-50 rounded-lg p-6 mb-6">
                                            <p className="text-2xl font-bold text-primary-600 mb-1">
                                                ${APP_CONFIG.SUBSCRIPTION_PRICE}/month
                                            </p>
                                            <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-green-100 text-green-800 mb-4">
                                                {APP_CONFIG.SUBSCRIPTION_DISCOUNT_PERCENT}% OFF regular pricing
                                            </span>
                                            <ul className="text-left space-y-2 mb-4">
                                                <li className="flex items-center">
                                                    <svg
                                                        className="w-5 h-5 text-green-500 mr-2"
                                                        fill="currentColor"
                                                        viewBox="0 0 20 20"
                                                    >
                                                        <path
                                                            fillRule="evenodd"
                                                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                                            clipRule="evenodd"
                                                        />
                                                    </svg>
                                                    4 million tokens per month
                                                </li>
                                                <li className="flex items-center">
                                                    <svg
                                                        className="w-5 h-5 text-green-500 mr-2"
                                                        fill="currentColor"
                                                        viewBox="0 0 20 20"
                                                    >
                                                        <path
                                                            fillRule="evenodd"
                                                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                                            clipRule="evenodd"
                                                        />
                                                    </svg>
                                                    Advanced AI-powered meal
                                                    planning
                                                </li>
                                                <li className="flex items-center">
                                                    <svg
                                                        className="w-5 h-5 text-green-500 mr-2"
                                                        fill="currentColor"
                                                        viewBox="0 0 20 20"
                                                    >
                                                        <path
                                                            fillRule="evenodd"
                                                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                                            clipRule="evenodd"
                                                        />
                                                    </svg>
                                                    Comprehensive nutrition tracking
                                                </li>
                                            </ul>
                                            <p className="text-sm text-gray-500">
                                                Regular price: $2.50 per million
                                                tokens
                                            </p>
                                        </div>

                                        <button
                                            onClick={() => {
                                                setSkipFreeTrial(canGetFreeTrial);
                                                setShowSubscriptionModal(true);
                                            }}
                                            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                                        >
                                            {canGetFreeTrial
                                                ? "Subscribe Now"
                                                : hasUsedFreeTrial || dbUser?.stripe_customer_id
                                                ? "Subscribe Now"
                                                : "Start Free Trial"}
                                        </button>

                                        {dbUser?.stripe_customer_id && (
                                            <p className="mt-3 text-sm text-gray-500">
                                                We'll use your payment method on
                                                file
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
                )}

                <SubscriptionModal
                    open={showSubscriptionModal}
                    onClose={() => {
                        setShowSubscriptionModal(false);
                        setSkipFreeTrial(false);
                    }}
                    onSuccess={handleSubscriptionSuccess}
                    skipFreeTrial={skipFreeTrial}
                />

                <ConfirmationModal
                    isOpen={showCancelConfirmation}
                    onClose={() => setShowCancelConfirmation(false)}
                    onConfirm={handleCancelSubscription}
                    title="Cancel Subscription"
                    message="Are you sure you want to cancel your subscription? You'll continue to have access until the end of your current billing period."
                    confirmText="Yes, Cancel Subscription"
                    cancelText="Keep Subscription"
                    confirmButtonClass="bg-red-600 hover:bg-red-700 text-white focus:ring-red-500"
                    isLoading={cancelling}
                />
            </div>
        );
    };

    if (loading) {
        return (
            <div
                style={{
                    maxWidth: "1200px",
                    margin: "0 auto",
                    padding: "24px",
                }}
            >
                <div style={{ textAlign: "center", padding: "48px" }}>
                    <div style={{ fontSize: "18px", color: "#6b7280" }}>
                        Loading profiles...
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "24px" }}>
            {/* Tab Navigation */}
            <div
                style={{
                    borderBottom: "1px solid #e5e7eb",
                    marginBottom: "24px",
                }}
            >
                <nav style={{ display: "flex", gap: "32px" }}>
                    <button
                        onClick={() => navigate("/profile/profiles")}
                        style={{
                            padding: "8px 4px",
                            borderBottom:
                                activeTab === "profiles"
                                    ? "2px solid #3b82f6"
                                    : "2px solid transparent",
                            color:
                                activeTab === "profiles"
                                    ? "#3b82f6"
                                    : "#6b7280",
                            fontWeight: 500,
                            fontSize: "14px",
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                        }}
                    >
                        👤 Profiles
                    </button>
                    <button
                        onClick={() => navigate("/profile/config")}
                        style={{
                            padding: "8px 4px",
                            borderBottom:
                                activeTab === "config"
                                    ? "2px solid #3b82f6"
                                    : "2px solid transparent",
                            color:
                                activeTab === "config" ? "#3b82f6" : "#6b7280",
                            fontWeight: 500,
                            fontSize: "14px",
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                        }}
                    >
                        ⚙️ AI Configuration
                    </button>
                    <button
                        onClick={() => navigate("/profile/billing")}
                        style={{
                            padding: "8px 4px",
                            borderBottom:
                                activeTab === "billing"
                                    ? "2px solid #3b82f6"
                                    : "2px solid transparent",
                            color:
                                activeTab === "billing" ? "#3b82f6" : "#6b7280",
                            fontWeight: 500,
                            fontSize: "14px",
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                        }}
                    >
                        💳 Billing
                    </button>
                </nav>
            </div>

            {/* Tab Content */}
            {activeTab === "profiles" && (
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "300px 1fr",
                        gap: "24px",
                    }}
                >
                    {/* Profile Sidebar */}
                    <div
                        style={{
                            backgroundColor: "white",
                            borderRadius: "8px",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                            height: "fit-content",
                        }}
                    >
                        <div
                            style={{
                                padding: "16px 20px",
                                borderBottom: "1px solid #e5e7eb",
                            }}
                        >
                            <h3
                                style={{
                                    fontSize: "16px",
                                    fontWeight: 600,
                                    margin: 0,
                                }}
                            >
                                Your Profiles
                            </h3>
                            <p
                                style={{
                                    fontSize: "14px",
                                    color: "#6b7280",
                                    margin: "4px 0 0 0",
                                }}
                            >
                                Manage meal planning profiles
                            </p>
                        </div>

                        <div style={{ padding: "16px 20px" }}>
                            {/* Profile List */}
                            <div style={{ marginBottom: "16px" }}>
                                {profiles.map((profile, index) => (
                                    <div
                                        key={profile.id}
                                        onClick={() =>
                                            setSelectedProfile(profile)
                                        }
                                        style={{
                                            padding: "12px",
                                            marginBottom: "8px",
                                            borderRadius: "6px",
                                            border:
                                                selectedProfile?.id ===
                                                profile.id
                                                    ? "2px solid #3b82f6"
                                                    : "1px solid #e5e7eb",
                                            backgroundColor:
                                                selectedProfile?.id ===
                                                profile.id
                                                    ? "#eff6ff"
                                                    : "white",
                                            cursor: "pointer",
                                            transition: "all 0.2s",
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                            }}
                                        >
                                            <div>
                                                <div
                                                    style={{
                                                        fontWeight: 500,
                                                        fontSize: "14px",
                                                    }}
                                                >
                                                    {profile.name}
                                                    {index === 0 && (
                                                        <span
                                                            style={{
                                                                marginLeft:
                                                                    "6px",
                                                                fontSize:
                                                                    "11px",
                                                                backgroundColor:
                                                                    "#3b82f6",
                                                                color: "white",
                                                                padding:
                                                                    "1px 4px",
                                                                borderRadius:
                                                                    "3px",
                                                            }}
                                                        >
                                                            Main
                                                        </span>
                                                    )}
                                                </div>
                                                <div
                                                    style={{
                                                        fontSize: "12px",
                                                        color: "#6b7280",
                                                    }}
                                                >
                                                    Created{" "}
                                                    {new Date(
                                                        profile.created_at
                                                    ).toLocaleDateString()}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Add New Profile */}
                            {isCreatingProfile ? (
                                <div style={{ marginBottom: "16px" }}>
                                    <input
                                        type="text"
                                        placeholder="Profile name (e.g., John Doe)"
                                        value={newProfileName}
                                        onChange={(e) =>
                                            setNewProfileName(e.target.value)
                                        }
                                        autoFocus
                                        style={{
                                            width: "100%",
                                            padding: "8px 12px",
                                            border: "1px solid #d1d5db",
                                            borderRadius: "6px",
                                            fontSize: "14px",
                                            marginBottom: "8px",
                                        }}
                                    />
                                    <div
                                        style={{ display: "flex", gap: "8px" }}
                                    >
                                        <button
                                            onClick={handleCreateProfile}
                                            disabled={!newProfileName.trim()}
                                            style={{
                                                flex: 1,
                                                backgroundColor: "#3b82f6",
                                                color: "white",
                                                padding: "6px 12px",
                                                borderRadius: "4px",
                                                border: "none",
                                                fontSize: "12px",
                                                cursor: "pointer",
                                                opacity: !newProfileName.trim()
                                                    ? 0.5
                                                    : 1,
                                            }}
                                        >
                                            Create
                                        </button>
                                        <button
                                            onClick={() => {
                                                setIsCreatingProfile(false);
                                                setNewProfileName("");
                                            }}
                                            style={{
                                                flex: 1,
                                                backgroundColor: "#f3f4f6",
                                                color: "#374151",
                                                padding: "6px 12px",
                                                borderRadius: "4px",
                                                border: "none",
                                                fontSize: "12px",
                                                cursor: "pointer",
                                            }}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <button
                                    onClick={() => setIsCreatingProfile(true)}
                                    style={{
                                        width: "100%",
                                        padding: "12px",
                                        border: "2px dashed #d1d5db",
                                        borderRadius: "6px",
                                        backgroundColor: "transparent",
                                        color: "#6b7280",
                                        fontSize: "14px",
                                        cursor: "pointer",
                                        transition: "colors 0.2s",
                                    }}
                                >
                                    + Add New Profile
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Profile Form */}
                    <div>
                        {selectedProfile ? (
                            <ProfileForm
                                profile={selectedProfile}
                                isMainProfile={
                                    selectedProfile.id === profiles[0]?.id
                                }
                                onSave={handleSaveProfile}
                                onDelete={
                                    selectedProfile.id !== profiles[0]?.id
                                        ? () =>
                                              handleDeleteProfile(
                                                  selectedProfile
                                              )
                                        : undefined
                                }
                            />
                        ) : (
                            <div
                                style={{
                                    backgroundColor: "white",
                                    borderRadius: "8px",
                                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                    padding: "48px",
                                    textAlign: "center",
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: "48px",
                                        marginBottom: "16px",
                                    }}
                                >
                                    👤
                                </div>
                                <div
                                    style={{
                                        fontSize: "18px",
                                        color: "#6b7280",
                                    }}
                                >
                                    Select a profile to edit
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {activeTab === "config" && <ApiConfigTab />}

            {activeTab === "billing" && <BillingTab />}
        </div>
    );
};

export default ProfileManager;
